import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMediaPredicate } from 'react-media-hook';
import { useSearchParams } from 'react-router-dom';
import parse from 'html-react-parser';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { Button } from 'components/ui/button';
import { ScrollArea } from 'components/ui/scroll-area';
import {
  ColorRange,
  ParameterHistory,
  ParameterHistoryParams,
} from 'services/face-scan/type';
import useAuthStore from 'store/auth';
import {
  CategorizedReadingData,
  ReadingData,
  ReadingDataWithSubParameters,
} from 'types';
import LineChart from 'components/charts/LineChart';
import ReportActions from '../ReportActions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { cn } from 'lib/utils';
import ScanVitalDetail from './ScanVitalDetail';

interface VitalSignDetailProps {
  readingId: string;
  historyParams: ParameterHistoryParams;
  data: CategorizedReadingData;
  scanData: ReadingDataWithSubParameters[];
  refetchScanReports: () => void;
  setSelectedReadingId: (id: string) => void;
  setHistoryParams: Dispatch<SetStateAction<ParameterHistoryParams>>;
  isParameterFetching?: boolean;
}

interface HistoryData {
  data: ParameterHistory[];
  colorRanges: ColorRange[];
}

export default function VitalSignDetail({
  data,
  readingId,
  scanData,
  historyParams,
  refetchScanReports,
  setSelectedReadingId,
  setHistoryParams,
  isParameterFetching,
}: VitalSignDetailProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const [searchParams, setSearchParams] = useSearchParams();
  const vitalKey = searchParams.get('vital') || '';

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const parameterHistory =
    queryClient.getQueryData(['parameter-history', profileId, historyParams]) ||
    null;

  const [historyData, setHistoryData] = useState<HistoryData>({
    data: [],
    colorRanges: [],
  });
  const [bpHistoryData, setBpHistoryData] = useState<
    Record<'diastolic' | 'systolic', HistoryData>
  >({
    diastolic: { data: [], colorRanges: [] },
    systolic: { data: [], colorRanges: [] },
  });
  const [selectedVital, setSelectedVital] = useState<ReadingData>();

  const onVitalSelect = (vitalKey: string) => {
    if (selectedVital && selectedVital.key === vitalKey) {
      setSelectedVital(undefined);
      setHistoryData({ data: [], colorRanges: [] });
      return;
    }

    const vital = scanData?.find((data) => data.key === vitalKey);
    setSelectedVital(vital);

    if (parameterHistory) {
      const history =
        parameterHistory[vitalKey as keyof typeof parameterHistory];
      setHistoryData({
        data: [...history].reverse(),
        colorRanges: vital?.colorRange ?? [],
      });
    }
  };

  const historyOptions = [
    { label: t('last_7'), value: 'last_7' },
    { label: t('last_5'), value: 'last_5' },
    { label: t('last_2'), value: 'last_2' },
    { label: t('this_month'), value: 'this_month' },
    { label: t('this_quarter'), value: 'this_quarter' },
  ];

  const historyValue = () =>
    (historyParams &&
      historyOptions.find((opt) => opt.value === historyParams.type)?.label) ||
    '';

  const onHistoryFilter = (value: string) => {
    setHistoryParams?.({ type: value });
  };

  useEffect(() => {
    if (vitalKey && scanData && scanData.length) {
      onVitalSelect(vitalKey);
      if (isMediaMobile) setSearchParams({});
    }
  }, [vitalKey, scanData, isMediaMobile]);

  useEffect(() => {
    if (parameterHistory && selectedVital?.key) {
      if (selectedVital.key === 'BLOOD_PRESSURE') {
        setHistoryData({ data: [], colorRanges: [] });
        const systolicHistory = (parameterHistory as any)['systolic'];
        const diastolicHistory = (parameterHistory as any)['diastolic'];

        setBpHistoryData({
          systolic: {
            data: systolicHistory ? [...systolicHistory].reverse() : [],
            colorRanges: selectedVital.colorRange ?? [],
          },
          diastolic: {
            data: diastolicHistory ? [...diastolicHistory].reverse() : [],
            colorRanges: selectedVital.colorRange ?? [],
          },
        });
      } else {
        setBpHistoryData({
          systolic: { data: [], colorRanges: [] },
          diastolic: { data: [], colorRanges: [] },
        });
        const history =
          parameterHistory[selectedVital.key as keyof typeof parameterHistory];

        setHistoryData({
          data: history ? [...history].reverse() : [],
          colorRanges:
            history && selectedVital?.colorRange
              ? selectedVital.colorRange
              : [],
        });
      }
    }
  }, [selectedVital, parameterHistory]);

  if (selectedVital) {
    return (
      <div className='p-4 sm:hidden'>
        <div className='flex justify-end'>
          <Button
            variant='ghost'
            className='text-sm text-blue-500 uppercase hover:text-blue-500 h-fit'
            onClick={() => setSelectedVital(undefined)}
          >
            <ChevronLeft className='w-4 h-4 mr-2' /> {t('scan_report')}
          </Button>
        </div>

        <ScrollArea className='h-[calc(100vh-10rem)]'>
          <Accordion defaultValue='info' type='single' collapsible>
            <AccordionItem value='info'>
              <AccordionTrigger>{t('parameter_info')}</AccordionTrigger>
              <AccordionContent>
                <div className='space-y-4 font-medium'>
                  <p className='text-primary'>{selectedVital?.short_intro}</p>

                  <div
                    className='whitespace-pre-wrap'
                    style={{ wordBreak: 'break-word' }}
                  >
                    {selectedVital?.long_intro
                      ? selectedVital.long_intro
                          .split('\n')
                          ?.map((line, ind) => {
                            if (!line.trim()) {
                              return (
                                <div
                                  className='block w-1/2 h-4'
                                  key={`${line}-${ind}`}
                                />
                              );
                            }

                            return parse(line, {
                              replace(domNode: any) {
                                if (domNode?.data?.startsWith('https://')) {
                                  return (
                                    <a
                                      href={domNode.data}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='block text-blue-500'
                                    >
                                      {domNode.data}
                                    </a>
                                  );
                                }
                              },
                            });
                          })
                      : null}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            {selectedVital?.colorRangeWithCategory?.length ? (
              <AccordionItem value='ranges'>
                <AccordionTrigger>{t('parameter_ranges')}</AccordionTrigger>
                <AccordionContent>
                  {selectedVital?.colorRangeWithCategory?.map(
                    ({ color, range, eng_category }, index) => {
                      return (
                        <div
                          key={index}
                          className={cn(
                            'flex items-center justify-between py-2 font-medium border-b',
                            index === 0 && 'border-t'
                          )}
                        >
                          <div className='flex items-center gap-x-2'>
                            <div
                              className='w-4 h-4 rounded-full'
                              style={{ backgroundColor: color }}
                            />
                            <p className='text-sm'>{eng_category}</p>
                          </div>

                          <div>
                            {range.length > 1
                              ? `${range[0]} - ${range[1]}`
                              : String(range[0])
                                  .replace('>', 'Higher than ')
                                  .replace('<', 'Lower than ')}
                          </div>
                        </div>
                      );
                    }
                  )}
                </AccordionContent>
              </AccordionItem>
            ) : null}

            <AccordionItem value='history'>
              <AccordionTrigger>{t('parameter_history')}</AccordionTrigger>
              <AccordionContent>
                <div className='flex justify-end mb-2 mr-2'>
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      <p className='text-sm font-medium text-primary'>
                        {t('range')}: {historyValue()}
                      </p>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {historyOptions.map(({ label, value }) => (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onHistoryFilter(value);
                          }}
                          key={value}
                        >
                          {label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div
                  className={cn(
                    'w-full h-48 p-4 border border-primary rounded-3xl',
                    selectedVital?.key === 'BLOOD_PRESSURE' && 'lg:h-96'
                  )}
                >
                  {isParameterFetching ? (
                    <div className='grid w-full h-full place-items-center'>
                      <Loader2 className='stroke-1 animate-spin size-6 text-primary' />
                    </div>
                  ) : (
                    <>
                      {selectedVital?.key === 'BLOOD_PRESSURE' ? (
                        <>
                          {bpHistoryData.diastolic.data.length &&
                          bpHistoryData.systolic.data.length ? (
                            Object.keys(bpHistoryData).map((bp) => (
                              <div key={bp}>
                                <LineChart
                                  {...bpHistoryData[
                                    bp as keyof typeof bpHistoryData
                                  ]}
                                />
                                <p className='font-medium text-center capitalize'>
                                  {bp}
                                </p>
                              </div>
                            ))
                          ) : (
                            <div className='grid w-full h-full place-items-center'>
                              <p className='font-medium text-muted-foreground'>
                                {t('no_history')}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {historyData?.data?.length ? (
                            <LineChart {...historyData} />
                          ) : (
                            <div className='grid w-full h-full place-items-center'>
                              <p className='font-medium text-muted-foreground'>
                                {t('no_history')}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className='p-4 sm:hidden'>
      <div className='flex justify-end'>
        <Button
          variant='ghost'
          className='text-sm text-blue-500 uppercase hover:text-blue-500 h-fit'
          onClick={() => setSelectedReadingId('')}
        >
          <ChevronLeft className='w-4 h-4 mr-2' /> {t('previous_scans')}
        </Button>
      </div>

      <ReportActions
        scanData={scanData}
        readingId={readingId}
        refetchScanReports={refetchScanReports}
      />

      <ScrollArea className='h-[calc(100vh-10rem)]'>
        {Object.keys(data)?.map((category) => {
          return (
            <div key={category}>
              <h2 className='text-xl font-semibold'>{category}</h2>

              {data[category]?.length ? (
                <div className='p-4 mt-2 ml-2 border-l-2 border-l-primary'>
                  {data[category]?.map((vital) => (
                    <ScanVitalDetail
                      vital={vital}
                      onSelect={onVitalSelect}
                      key={vital.key}
                      selectedVital={selectedVital}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </ScrollArea>
    </div>
  );
}
